import { Icon } from '@aqarmap/aqarmap-ui';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useBreakpoint, useFullRoutes } from '~/hooks';
import { ValuationItemType } from '~/types/valuationTypes';
import { ValuationTypeEnum } from '~/constants/enums/valuationEnums';

const ValuationResultsInfo = ({
  valuationData,
}: {
  valuationData: ValuationItemType;
}) => {
  const { mobileScreen } = useBreakpoint();
  const { t } = useTranslation('app');
  const { ADD_LISTING_URL } = useFullRoutes();
  const valuationPrice = valuationData.currentValuation.valuationPrice;
  const area = valuationData.area;

  return (
    <div className="border border-gray rounded-[16px] px-2x md:px-3x py-3x flex flex-col gap-3x">
      <div className="flex justify-between">
        {valuationData.type === ValuationTypeEnum.PORTFOLIO ? (
          <div className="flex flex-col gap-1.5x">
            <p className="text-body_2 text-gray__dark_2">
              {t('currentValuationSalePrice')}
            </p>
            <div className="flex gap-x items-center">
              <Icon
                name="money"
                size={mobileScreen ? '32px' : '40px'}
                className="text-gray__dark_2 "
              />
              <div className="flex gap-1 md:gap-x">
                <p className="text-title_3">
                  {valuationPrice.toLocaleString()}
                </p>
                <p className="text-title_4 md:text-title_3 self-center md:self-end">
                  {t('pound')}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-2x overflow-hidden w-full">
            <div className="flex gap-3x">
              <p className="text-title_4 md:text-title_3 truncate">
                {valuationData.location.titleShortPath}{' '}
              </p>
              <div className="flex gap-x items-center px-1.5x py-1 rounded-[6px] bg-gray w-max text-caption text-gray__dark_2">
                <p>{valuationData.propertyType.titleSingular}</p>
              </div>
            </div>
            <div className="flex justify-between w-full">
              <div className="flex gap-x items-center">
                <Icon
                  name="money"
                  size={mobileScreen ? '32px' : '40px'}
                  className="text-gray__dark_2 "
                />
                <div className="flex flex-col gap-x h-[72px]">
                  <p className="text-body_2 text-gray__dark_2">
                    {t('averagePricePerMeter')}
                  </p>
                  <p className="text-title_3 self-end">
                    {Number(valuationPrice / (area || 100)).toLocaleString(
                      'en-US',
                      {
                        maximumFractionDigits: 0,
                      },
                    )}
                    <span className="text-title_4 ps-x">
                      {t('pricePerMeterUnit')}
                    </span>
                  </p>
                </div>
              </div>
              <div
                className={`${valuationData.type === ValuationTypeEnum.ESTIMATION ? 'flex md:hidden' : ''} h-max md:h-[88px] py-x px-1.5x rounded-[8px] justify-center items-center border border-[#0CB54833] bg-[#0CB5481A] flex flex-col gap-1`}
              >
                <p className="text-caption text-gray__dark_2">
                  {t('confidenceRate')}
                </p>
                <p className="text-link text-green_dark_1">
                  {Number(valuationData.currentValuation.confidenceRate * 100)}%
                </p>
              </div>
            </div>
          </div>
        )}
        <div
          className={`${valuationData.type === ValuationTypeEnum.ESTIMATION ? 'hidden md:flex' : ''} h-max md:h-[88px] py-x md:py-1.5x px-1.5x md:px-3x rounded-[8px] justify-center items-center border border-[#0CB54833] bg-[#0CB5481A] flex flex-col gap-1 md:gap-2x`}
        >
          <p className="text-caption md:text-body_2 text-gray__dark_2 whitespace-nowrap">
            {t('confidenceRate')}
          </p>
          <p className="text-link md:text-title_4 text-green_dark_1">
            {valuationData.currentValuation.confidenceRate * 100}%
          </p>
        </div>
      </div>
      {valuationData.type === ValuationTypeEnum.PORTFOLIO && (
        <>
          <div className="flex gap-2x items-center">
            <p className="text-body_2 text-gray__dark_2">
              {t('currentValuationRentPrice')}
            </p>
            <div className="flex gap-x items-center px-2x py-1 rounded-[8px] bg-gray_light_1 w-max text-gray_light_2">
              <p>{t('soon')}</p>
            </div>
          </div>
          <div className="pt-2x md:pt-3x border-t border-gray flex flex-wrap justify-between">
            <div className="flex flex-col gap-x pb-3x md:pb-0 border-b md:border-none border-gray overflow-hidden">
              <div className="flex gap-x overflow-hidden w-full">
                <Icon name="location" size="24px" />
                <p className="text-body_2 text-gray__dark_2 truncate">
                  {valuationData.location.titleShortPath}
                </p>
              </div>
              <div className="flex gap-y-x gap-x-1.5x md:gap-3x flex-wrap">
                <div className="flex gap-x">
                  <Icon name="property-type" size="24px" />
                  <p className="text-body_2 text-gray__dark_2">
                    {valuationData.propertyType.titleSingular}
                  </p>
                </div>
                <div className="flex gap-x">
                  <Icon name="size" size="24px" />
                  <p className="text-body_2 text-gray__dark_2">
                    {valuationData.area} {t('m2')}
                  </p>
                </div>
                <div className="flex gap-x">
                  <Icon name="bedroom" size="24px" />
                  <p className="text-body_2 text-gray__dark_2">
                    {valuationData.rooms} {t('rooms')}
                  </p>
                </div>
                <div className="flex gap-x">
                  <Icon name="bathroom" size="24px" />
                  <p className="text-body_2 text-gray__dark_2">
                    {valuationData.baths} {t('bathroom')}
                  </p>
                </div>
                <div className="flex gap-x">
                  <Icon name="finish" size="24px" />
                  <p className="text-body_2 text-gray__dark_2">
                    {t(valuationData.finishType)}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3x w-full md:w-max  pt-3x md:pt-0 justify-center">
              <Link
                to={ADD_LISTING_URL.INITIALIZE}
                className="flex flex-col justify-center items-center gap-0.5x"
              >
                <Icon name="list" className="text-blue" size="32px" />
                <p className="text-link text-blue">{t('addListing')}</p>
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ValuationResultsInfo;
